import {default as AjaxPageConfig, equal as configsEqual} from "./AjaxPageConfig";
import UrlUtils from "../../lib/UrlUtil";

export enum LoadState {
    NotLoaded,
    Loading,
	Loaded,
	Error,
	Ready
}

// TODO: Think through what is private and protected, and how methods are broken up so this can be extended for different needs. Also make it so what class is used can be configured.
// TODO: Should probably set a max of how many pages can be loaded at once and remove the oldest which is not open. Setting? Since iframe should probably be lower.
export default class AjaxPage {
	public static isInitialized = false;

	private static instances: Map<string, AjaxPage>;
	private static loadingInstances: Set<AjaxPage>;
	private static showingInstances: Set<AjaxPage>;

	private readonly config: AjaxPageConfig;
	private useIframe: boolean;
	private contentSelector: string;
	private pageTitle: string;
	private pageUrl: string;

	private addedEscapeListener: boolean = false;

	private requestUrl: string;

	private loadState = LoadState.NotLoaded;
	private showWhenReady = true;
	private pageElement: Element;
	private pageBodyElement: Element;
	private closeElements: Element[];

	/**
	 * Returns or creates an instance of AjaxPage. Will not create one if config is not provided.
	 * If config is provided but an instance exists, it will be returned if the configs are equal.
	 * But if the configs are not equal, the existing one will be destroyed and a new one created.
	 */
	public static getInstance(url: string, config?: AjaxPageConfig): AjaxPage {
		// Make URL absolute, if not already
		const urlUtil = UrlUtils.getInstance();
		url = urlUtil.applyBaseUrl(url);

		// If no config porivded, just look for an existing instance.
		if (config === undefined) {
			if (url === undefined || url === null || url.length === 0) {
				return;
			}
			if (AjaxPage.instances === undefined) {
				return;
			}
			return AjaxPage.instances.get(url);
		}

		// Do some setup work, if this is the first time we are creating a config.
		if (AjaxPage.isInitialized === false) {
			AjaxPage.isInitialized = true;
			AjaxPage.showingInstances = new Set<AjaxPage>();
			AjaxPage.instances = new Map<string, AjaxPage>();
			this.onHistoryPopState = this.onHistoryPopState.bind(this);
			window.addEventListener("popstate", this.onHistoryPopState);
			history.replaceState({ajaxPageUrl: window.location.href}, window.document.title, window.location.href);
		}

		// Look for existing
		let ajaxPage = AjaxPage.instances.get(url);

		// Return existing if same config, otherwise destroy before we create a new instance
		if (ajaxPage !== undefined) {
			if (configsEqual(config, ajaxPage.config) === true) {
				return ajaxPage;
			} else {
				ajaxPage.destroy(); // We are going to recreate with new config
			}
		}

		// Create a new instance and save instance
		ajaxPage = new AjaxPage(url, config);
		AjaxPage.instances.set(url, ajaxPage);

		return ajaxPage;
	}

	private static onHistoryPopState(ev: PopStateEvent) {
		const newAjaxPageUrl = ev.state?.ajaxPageUrl as string;
		const newAjaxPage = AjaxPage.getInstance(newAjaxPageUrl);

		AjaxPage.showingInstances.forEach((ajaxPage: AjaxPage) => {
			if (ajaxPage !== newAjaxPage) {
				ajaxPage.hide();
			}
		});

		if (newAjaxPage !== undefined) {
			newAjaxPage.show();
		}
	}


	protected constructor(url: string, config: AjaxPageConfig) {
		this.pageUrl = url;
		this.config = config;

		let contentSelector;
		let useIframe;
		if (typeof config.isIframeLoadingOrContentSelector === "string") {
			contentSelector = config.isIframeLoadingOrContentSelector;
			useIframe = false;
		} else {
			contentSelector = null;
			useIframe = config.isIframeLoadingOrContentSelector;
		}

		this.useIframe = useIframe;
		this.contentSelector = contentSelector;
	}

	protected getRequestUrl(): string {
		if (this.requestUrl === undefined) {
			const urlUtil = UrlUtils.getInstance();
			const urlObj = urlUtil.toUrlObj(this.pageUrl);
			const params = urlObj.searchParams;
			params.set("isAjaxPage", "true");
			if (this.useIframe === true) {
				params.set("isAjaxPageIFramed", "true");
			}
			if (this.contentSelector !== null) {
				params.set("ajaxPageContentSelector", encodeURI(this.contentSelector));
			}
			if (this.config.requestParams !== undefined && this.config.requestParams !== null) {
				for (const key in this.config.requestParams) {
					const value = this.config.requestParams[key];
					params.set(key, value);
				}
			}
			this.requestUrl = urlUtil.toUrlString(urlObj, params);
		}
		return this.requestUrl;
	}

	private setLoadState(loadState: LoadState) {
		this.loadState = loadState;
		this.updateLoadingStateStyleClass();
		if (this.config.onLoadStateChange !== undefined && this.config.onLoadStateChange !== null) {
			this.config.onLoadStateChange.call(this, this, this.loadState);
		}
	}
	private updateLoadingStateStyleClass() {
		if (this.loadState === LoadState.Loading) {
			if (AjaxPage.loadingInstances === undefined) {
				AjaxPage.loadingInstances = new Set<AjaxPage>();
			}
			AjaxPage.loadingInstances.add(this);
			document.documentElement.classList.add("isAjaxPageLoadingToShow");
		} else if (this.loadState === LoadState.Loaded || this.loadState === LoadState.Error) {
			if (AjaxPage.loadingInstances !== undefined) {
				AjaxPage.loadingInstances.delete(this);
				if (AjaxPage.loadingInstances.size === 0) {
					document.documentElement.classList.remove("isAjaxPageLoadingToShow");
				}
			}
		}
	}
	
	public load(showWhenReady: boolean = false) {
		this.showWhenReady = showWhenReady;

		if (this.loadState >= LoadState.Loading) {
			this.updateLoadingStateStyleClass();
			return;
		}
		this.setLoadState(LoadState.Loading);

		if (this.useIframe === true) {

			const bodyElem = this.getPageBodyElement();
			if (bodyElem instanceof HTMLIFrameElement) {
				const iframeElem = bodyElem as HTMLIFrameElement;
				iframeElem.addEventListener("load", (ev: Event) => {
					this.setLoadState(LoadState.Loaded);

					const onDomReady = (doc: Document) => {
						this.pageTitle = doc.title;
						
						const anchors = doc.querySelectorAll("a");
						for (let i = 0; i < anchors.length; i++) {
							const anchor = anchors[i];
							if (anchor.target === null || anchor.target === undefined || anchor.target.length === 0) {
								anchor.target = "_parent";
							}
						}
						
						this.onReady();
						if (this.showWhenReady === true) {
							this.show();
						}
					}

					const iframeDoc = iframeElem.contentWindow.document;
					if (iframeDoc.readyState === "interactive" || iframeDoc.readyState === "complete") {
						onDomReady(iframeDoc);
					} else {
						iframeDoc.addEventListener("DOMContentLoaded", (ev: Event) => {
							onDomReady(iframeDoc);
						});
					}
				});
				
				iframeElem.src = this.getRequestUrl();
			} else {
				this.setLoadState(LoadState.Error);
				throw new Error(`AjaxPage is loading using an iframe but getPageBodyElement() did not return an HTMLIFrameElement.`);
			}

		} else {

			const httpRequest = new XMLHttpRequest();
			httpRequest.onreadystatechange = (ev: Event) => {
				if (httpRequest.readyState !== XMLHttpRequest.DONE) {
					return;
				}
				if (httpRequest.status !== 200) {
					this.setLoadState(LoadState.Error);
					throw new Error(`Error opening XMLHttpRequest for "${this.pageUrl}"`);
				}
		
				const doc = httpRequest.responseXML;

				this.pageTitle = doc.title;

				const bodyElem = doc.body;
				if (bodyElem === null) {
					this.setLoadState(LoadState.Error);
					throw new Error(`Response for "${this.pageUrl}" does not have a "body" element.`);
				}
				let bodyContentElem: Element = null;
				if (this.contentSelector !== null && this.contentSelector !== undefined && this.contentSelector.length !== 0) {
					let selectedElem = bodyElem.querySelector(this.contentSelector);
					if (selectedElem !== null) {
						bodyContentElem = selectedElem;
					} else {
						this.setLoadState(LoadState.Error);
						throw new Error(`Response for "${this.pageUrl}" does not have an element which matches query selector: ${this.contentSelector}`);
					}
				} else {
					bodyContentElem = bodyElem;
				}

				let pageBodyElem = this.getPageBodyElement();
				if (pageBodyElem.firstChild !== null) {
					pageBodyElem.textContent = ""; // Clear all contents, if there is any
				}
				pageBodyElem.appendChild(bodyContentElem);

				this.setLoadState(LoadState.Loaded);

				window.setTimeout(
					() => {
						this.onReady();
						if (this.showWhenReady === true) {
							this.show();
						}
					}, 0
				);
			};

			httpRequest.open("GET", this.getRequestUrl(), true);
			httpRequest.responseType = "document";
			httpRequest.send();

		}
	}

	protected createElements() {
		// Close elements
		if (this.closeElements === undefined) {
			this.closeElements = new Array<Element>();
		}

		// page element
		if (this.pageElement === undefined) {
			this.pageElement = document.createElement("div");
			this.pageElement.classList.add("ajaxPage");
			this.closeElements.push(this.pageElement);

			const closeElem = document.createElement("div");
			closeElem.classList.add("ajaxPage__close");
			closeElem.textContent = this.config.closeButtonText;
			this.pageElement.appendChild(closeElem);
			this.closeElements.push(closeElem);

			if (this.useIframe) {
				this.pageElement.classList.add("ajaxPage--hasIframe");
				this.pageBodyElement = document.createElement("iframe");
				this.pageBodyElement.classList.add("ajaxPage__body");
				this.pageElement.appendChild(this.pageBodyElement);
			} else {
				this.pageBodyElement = document.createElement("div");
				this.pageBodyElement.classList.add("ajaxPage__body");
				this.pageElement.appendChild(this.pageBodyElement);
			}
			document.body.appendChild(this.pageElement);
		}

		// overlay
		let overlayElem = document.querySelector(".ajaxPageOverlay");
		if (overlayElem === null) {
			// Create one if one does not exist. We share one across all AjaxPage instances.
			overlayElem = document.createElement("div");
			overlayElem.classList.add("ajaxPageOverlay");
			overlayElem.addEventListener("click", (ev: MouseEvent) => {
				ev.preventDefault();
				ev.stopPropagation();
			});
			const loaderAnimElem = document.createElement("div");
			loaderAnimElem.classList.add("loaderAnimation");
			overlayElem.appendChild(loaderAnimElem);
			document.body.appendChild(overlayElem);
		}
	}
	protected getPageElement(): Element {
		if (this.pageElement === undefined) {
			this.createElements();
		}
		return this.pageElement;
	}
	public getWindow(): Window {
		const bodyElem = this.getPageBodyElement();
		if (this.useIframe === true) {
			const iframeElem = bodyElem as HTMLIFrameElement;
			return iframeElem.contentWindow;
		} else {
			return window;
		}
	}
	protected getPageBodyElement(): Element {
		if (this.pageBodyElement === undefined) {
			this.createElements();
		}
		return this.pageBodyElement;
	}
	protected getCloseElements(): Element[] {
		if (this.closeElements === undefined) {
			this.createElements();
		}
		return this.closeElements;
	}

	protected onReady() {
		// Setup all close elements (e.g. overlay and close button)
		const closeElems = this.getCloseElements();
		for (let i = 0; i < closeElems.length; i++) {
			const closeElem = closeElems[i];
			closeElem.addEventListener("click", (ev: MouseEvent) => {
				ev.preventDefault();
				ev.stopPropagation();
				this.hide();
			});
		}

		if (!this.addedEscapeListener) {
			this.addedEscapeListener = true;
			document.addEventListener('keyup', e => {
				if (e.key === 'Escape' && AjaxPage.showingInstances.size > 0) {
					const activeName = document.activeElement.tagName.toLowerCase();
					if (activeName !== 'input' && activeName !== 'select') {
						const event = document.createEvent('Event');
						event.initEvent('click', true, true);
						this.getCloseElements()[0].dispatchEvent(event);
					}
				}
			});
		}

		// Update state to ready
		this.setLoadState(LoadState.Ready);
	}

	public show(): boolean {
		if (this.loadState < LoadState.Ready) {
			this.load(true);
			return false;
		}
		if (location.href !== this.pageUrl) {
			history.pushState({ajaxPageUrl: this.pageUrl}, this.pageTitle, this.pageUrl);
		}
		window.document.title = this.pageTitle;
		document.documentElement.classList.add("isAjaxPageShown");
		this.getPageElement().classList.add("ajaxPage--isShown");
		AjaxPage.showingInstances.add(this);
		return true;
	}
	public hide() {
		if (location.href === this.pageUrl) {
			history.back();
		} else if (AjaxPage.showingInstances.has(this) === true) {
			document.documentElement.classList.remove("isAjaxPageShown");
			this.getPageElement().classList.remove("ajaxPage--isShown");
			AjaxPage.showingInstances.delete(this);
		}
	}

	public destroy() {
		if (this.pageElement !== undefined) {
			this.pageElement.remove();
			this.pageElement = undefined;
			this.pageBodyElement = undefined;
			this.closeElements = undefined;
		}
		AjaxPage.instances.delete(this.pageUrl);
		AjaxPage.loadingInstances.delete(this);
		AjaxPage.showingInstances.delete(this);
	}
}


// This is ran no matter if AjaxPage is used or not on that page.
// This watches for a popstate related to AjaxPage, and if there is on
// but AjaxPage has not be initialized, we cause the page to reload.
// This is needed to avoid an issue where hitting the back button
// after reloading a page that was "ajaxed" in may not cause new content
// to load but instead just update the URL only.
window.addEventListener("popstate", (ev: PopStateEvent) => {
	if (
		AjaxPage.isInitialized === false
		&& ev.state !== null && ev.state !== undefined
		&& ev.state.ajaxPageUrl !== undefined
	) {
		history.go(); // Cause a reload
	}
});
